/**
 * Nouvelle intervention — UN volet, aucune fenêtre par-dessus (ADR-0016 ; né du volet « Nouvelle visite » du lot PL1a).
 *
 * Praxedo demande une fenêtre pour le client, une pour le site, une pour chercher la machine, une pour planifier : ici
 * tout tient dans un seul volet, dans l'ordre où l'on y pense. On TAPE (« ntn sey »), on choisit une machine ou un site ;
 * choisir une machine remplit le client et le site ; choisir un site propose ses machines à cocher. Puis quoi (la nature),
 * combien de temps, qui, et QUAND — trois façons, dont « date fixe, technicien plus tard » (le cas d'un arrêt d'usine
 * lointain dont on ne sait pas encore qui ira) et « fenêtre d'échéance, sans date » (le cas d'un contrôle dû dans les
 * trois mois). Rien n'est choisi d'avance sauf la date proposée (le lendemain ouvré) et ce que le geste d'origine impose.
 *
 * Toute la logique est dans `domaine/planning.ts` (testée) : ici, seulement de quoi montrer et recueillir. La création est
 * rejouable (clé d'idempotence tirée à l'ouverture) : un double clic ne crée pas deux visites. Fermer un volet touché
 * demande confirmation : un travail non enregistré ne se perd pas d'un clic de travers.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api, type Asset, type CreationIntervention, type NatureBon, type Site, type Technicien } from '../api/client';
import { aujourdhuiLocal } from '../domaine/interventions';
import {
  CARACTERES_MINIMUM,
  LIBELLE_MODE,
  ajouterMachine,
  basculerTechnicienFormulaire,
  choisirMode,
  choisirPalier,
  choisirSite,
  dateParDefaut,
  filtrerMachines,
  filtrerSites,
  formulaireModifie,
  formulaireVide,
  machineChoisie,
  motPourRecherche,
  oublierLieu,
  refusAjoutMachine,
  refusChoixSite,
  retirerMachine,
  validerFormulaire,
  type FormulaireVisite,
  type ModePlanification,
} from '../domaine/planning';
import { versEntreeIntervention } from '../domaine/planning-interventions';
import { infoPaliersMachine, phrasePieces, type InfoPaliersMachine } from '../domaine/planning-pieces';
import { resume as resumeEntretien } from '../domaine/prochain-entretien';
import { corpsDeModification, listeAvecPiecesEnPlus, type Liste } from '../domaine/lignes-intervention';
import { ErreurFormulaire, Panneau } from './Formulaire';
import { PiecesDemandeesALaCreation } from './PiecesDemandeesALaCreation';

const PAUSE_DE_FRAPPE_MS = 250;
/** Combien de machines et de sites on lit d'un coup : la recherche se précise en tapant. */
const MACHINES_LUES = 40;
const MACHINES_AFFICHEES = 10;
const SITES_AFFICHES = 8;
/** Les machines d'un site proposées à cocher : un site en porte rarement davantage. */
const MACHINES_DU_SITE = 50;

const MODES: readonly ModePlanification[] = ['DATE_TECHNICIEN', 'DATE_FIXE', 'FENETRE'];

export function VoletIntervention({
  depart,
  followUpId,
  onCreeeIntervention,
  onFermer,
}: {
  /** Le formulaire de départ (case de la grille, fiche d'une machine) ; sans lui, un formulaire vide à la date du lendemain. */
  depart?: FormulaireVisite;
  /** La suite à donner que cette intervention va traiter : elle n'est soldée qu'à la fin (intervention validée). */
  followUpId?: string;
  onCreeeIntervention?: (creation: CreationIntervention) => void;
  onFermer: () => void;
}) {
  const initial = useRef<FormulaireVisite>(depart ?? formulaireVide(dateParDefaut(aujourdhuiLocal())));
  const [f, setF] = useState<FormulaireVisite>(initial.current);
  /** Rend la création rejouable : une même ouverture du volet ne crée jamais deux interventions. */
  const cle = useRef(crypto.randomUUID());

  const [natures, setNatures] = useState<readonly NatureBon[] | null>(null);
  const [techniciens, setTechniciens] = useState<readonly Technicien[] | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  const [saisie, setSaisie] = useState('');
  const [resultats, setResultats] = useState<{ machines: readonly Asset[]; sites: readonly Site[]; plus: boolean } | null>(null);
  const [erreurRecherche, setErreurRecherche] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  const [machinesDuSite, setMachinesDuSite] = useState<readonly Asset[] | null>(null);
  const [indice, setIndice] = useState<string | null>(null);

  /** Les pièces en plus demandées par machine : elles partent avec « Créer l'intervention ». */
  const [enPlus, setEnPlus] = useState<Readonly<Record<string, Liste>>>({});
  const [tente, setTente] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);

  // Les natures et les techniciens se chargent une fois : ce sont des listes courtes.
  useEffect(() => {
    let annule = false;
    Promise.all([api.naturesBon(), api.techniciensPlanning()])
      .then(([n, t]) => {
        if (annule) return;
        setNatures(n.filter((x) => !x.archived));
        setTechniciens(t.items.filter((x) => !x.archived));
      })
      .catch((e: unknown) => {
        if (!annule) setErreurChargement(e instanceof ApiError ? e.message : 'Chargement impossible.');
      });
    return () => {
      annule = true;
    };
  }, []);

  // La recherche part après une pause de frappe ; une réponse périmée est jetée.
  useEffect(() => {
    const mot = motPourRecherche(saisie);
    if (mot === null) {
      setResultats(null);
      return;
    }
    let annule = false;
    const minuteur = window.setTimeout(() => {
      Promise.all([api.assets({ search: mot, take: MACHINES_LUES }), api.sites(mot)])
        .then(([a, s]) => {
          if (annule) return;
          setResultats({
            machines: filtrerMachines(a.items, saisie).slice(0, MACHINES_AFFICHEES),
            sites: filtrerSites(s, saisie).slice(0, SITES_AFFICHES),
            plus: a.total > a.items.length,
          });
          setErreurRecherche(null);
        })
        .catch((e: unknown) => {
          if (!annule) setErreurRecherche(e instanceof ApiError ? e.message : 'Recherche impossible.');
        });
    }, PAUSE_DE_FRAPPE_MS);
    return () => {
      annule = true;
      window.clearTimeout(minuteur);
    };
  }, [saisie]);

  // Le site choisi propose ses machines à cocher.
  const siteId = f.genre === 'CUSTOMER' ? f.siteId : null;
  useEffect(() => {
    if (siteId === null) {
      setMachinesDuSite(null);
      return;
    }
    let annule = false;
    api
      .assets({ siteId, take: MACHINES_DU_SITE })
      .then((r) => {
        if (!annule) setMachinesDuSite(r.items);
      })
      .catch(() => {
        if (!annule) setMachinesDuSite([]);
      });
    return () => {
      annule = true;
    };
  }, [siteId]);

  // Pour UNE machine, le prochain entretien en indice (jamais présélectionné : la nature et le palier restent un choix).
  const uneMachine = f.machines.length === 1 ? (f.machines[0]?.assetId ?? null) : null;
  useEffect(() => {
    if (uneMachine === null) {
      setIndice(null);
      return;
    }
    let annule = false;
    api
      .prochainEntretien(uneMachine)
      .then((e) => {
        if (!annule) setIndice(resumeEntretien(e));
      })
      .catch(() => {
        // Une machine sans gamme répond 404 : pas d'indice, ce n'est pas une erreur.
        if (!annule) setIndice(null);
      });
    return () => {
      annule = true;
    };
  }, [uneMachine]);

  // La gamme de chaque machine choisie (ses paliers, et celui que ses heures proposent), lue UNE fois par machine : de quoi choisir le
  // palier dont on veut préparer les pièces. Une machine sans gamme n'a rien à choisir ; une lecture qui échoue ne bloque pas la création.
  const [infos, setInfos] = useState<Readonly<Record<string, InfoPaliersMachine | 'ERREUR'>>>({});
  const dejaLues = useRef(new Set<string>());
  useEffect(() => {
    for (const m of f.machines) {
      if (dejaLues.current.has(m.assetId)) continue;
      dejaLues.current.add(m.assetId);
      const id = m.assetId;
      Promise.all([api.assetMaintenance(id), api.prochainEntretien(id).catch(() => null)])
        .then(([maintenance, prochain]) => setInfos((courantes) => ({ ...courantes, [id]: infoPaliersMachine(maintenance, prochain) })))
        .catch(() => setInfos((courantes) => ({ ...courantes, [id]: 'ERREUR' })));
    }
  }, [f.machines]);

  const messages = useMemo(() => validerFormulaire(f), [f]);

  const modifier = (suivant: FormulaireVisite) => {
    setF(suivant);
    setErreur(null);
  };

  const fermer = () => {
    if (formulaireModifie(f, initial.current) && !window.confirm('Abandonner cette intervention ? Ce que vous avez saisi sera perdu.')) return;
    onFermer();
  };

  const choisirUneMachine = (a: Asset) => {
    const m = machineChoisie(a);
    const motif = refusAjoutMachine(f, m);
    if (motif !== null) {
      setRefus(motif);
      return;
    }
    setRefus(null);
    modifier(ajouterMachine(f, m));
    setSaisie('');
    setResultats(null);
  };

  const choisirUnSite = (s: Site) => {
    const motif = refusChoixSite(f, s.id);
    if (motif !== null) {
      setRefus(motif);
      return;
    }
    setRefus(null);
    modifier(choisirSite(f, { id: s.id, libelle: s.name }));
    setSaisie('');
    setResultats(null);
  };

  const cocherMachineDuSite = (a: Asset, cochee: boolean) => {
    if (!cochee) {
      setRefus(null);
      modifier(retirerMachine(f, a.id));
      return;
    }
    const m = machineChoisie(a);
    const motif = refusAjoutMachine(f, m);
    if (motif !== null) {
      setRefus(motif);
      return;
    }
    setRefus(null);
    modifier(ajouterMachine(f, m));
  };

  const creer = async () => {
    setTente(true);
    if (messages.length > 0) return;
    setEnCours(true);
    setErreur(null);
    try {
      const creation = await api.creerIntervention({ ...versEntreeIntervention(f, cle.current), ...(followUpId ? { followUpId } : {}) });
      // Les pièces en plus : une écriture par machine, avec la liste COMPLÈTE (pièces du palier lues, puis les pièces ajoutées).
      const refusees: string[] = [];
      for (const cree of creation.interventions) {
        const extras = cree.assetId === null ? [] : (enPlus[cree.assetId] ?? []);
        if (extras.length === 0) continue;
        try {
          const b = await api.planningBriefingIntervention(cree.id);
          await api.modifierLignesIntervention(cree.id, corpsDeModification(b.intervention.revision, listeAvecPiecesEnPlus(b, extras)));
        } catch {
          refusees.push(...extras.map((p) => p.designation));
        }
      }
      if (refusees.length > 0) {
        window.alert(`L’intervention est créée, mais ${refusees.length > 1 ? 'ces pièces n’ont' : 'cette pièce n’a'} pas pu être ajoutée${refusees.length > 1 ? 's' : ''} : ${refusees.join(', ')}. Ajoutez-la depuis le détail de l’intervention (« Lignes »).`);
      }
      onCreeeIntervention?.(creation);
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Création impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const atelier = f.genre === 'WORKSHOP';
  const aUnLieu = f.siteId !== null || f.machines.length > 0;
  const technicienUtile = f.mode !== 'DATE_FIXE';

  return (
    <Panneau
      titre="Nouvelle intervention"
      sousTitre={
        followUpId
          ? 'Cette intervention traitera la suite à donner choisie : elle sera soldée quand l’intervention sera validée.'
          : 'Tapez le client, le site ou la machine ; le reste se règle en quelques gestes.'
      }
      onFermer={fermer}
      actions={
        <>
          <button type="button" className="secondaire" disabled={enCours} onClick={fermer}>
            Annuler
          </button>
          <button type="button" disabled={enCours} onClick={() => void creer()}>
            {enCours ? 'Création…' : 'Créer l’intervention'}
          </button>
        </>
      }
    >
      {erreurChargement && <div className="message erreur">{erreurChargement}</div>}

      <div className="chips-secondaires" role="group" aria-label="Genre d’intervention">
        <button
          type="button"
          className={`chip${!atelier ? ' actif' : ''}`}
          aria-pressed={!atelier}
          onClick={() => modifier({ ...f, genre: 'CUSTOMER' })}
        >
          Chez un client
        </button>
        <button
          type="button"
          className={`chip${atelier ? ' actif' : ''}`}
          aria-pressed={atelier}
          onClick={() => modifier({ ...f, genre: 'WORKSHOP' })}
          title="Préparation, rangement du camion, formation : du temps qui ne se passe pas chez un client"
        >
          Atelier
        </button>
      </div>

      {atelier ? (
        <div className="champ">
          <label htmlFor="visite-titre">Titre</label>
          <input
            id="visite-titre"
            type="text"
            autoFocus
            maxLength={120}
            value={f.titre}
            placeholder="Préparation des pièces, rangement du camion, formation…"
            onChange={(e) => modifier({ ...f, titre: e.target.value })}
          />
        </div>
      ) : (
        <>
          {!aUnLieu ? (
            <div className="champ">
              <label htmlFor="visite-recherche">Où ?</label>
              <input
                id="visite-recherche"
                type="text"
                autoFocus
                autoComplete="off"
                value={saisie}
                placeholder="Client, site ou machine : ntn sey, esd442, AIR01…"
                onChange={(e) => {
                  setSaisie(e.target.value);
                  setRefus(null);
                }}
                onKeyDown={(e) => {
                  // Échap ferme la liste, pas le volet : le panneau ignore un Échap déjà utilisé.
                  if (e.key === 'Escape' && saisie !== '') {
                    e.preventDefault();
                    setSaisie('');
                    setResultats(null);
                  }
                }}
              />
              <p className="aide">Tapez au moins {CARACTERES_MINIMUM} caractères ; plusieurs mots se cumulent.</p>
            </div>
          ) : (
            <div className="champ">
              <label>Où ?</label>
              <div className="visite-lieu">
                <strong>{f.siteLibelle ?? f.machines[0]?.siteLibelle ?? 'Site'}</strong>
                <button type="button" className="secondaire discret" onClick={() => modifier(oublierLieu(f))}>
                  Changer
                </button>
              </div>
              {f.machines.length > 0 && (
                <div className="visite-machines-choisies" role="group" aria-label="Machines de la visite">
                  {f.machines.map((m) => {
                    const info = infos[m.assetId];
                    return (
                      <div key={m.assetId} className="visite-machine-choisie">
                        <div className="visite-machine-entete">
                          <strong>{m.libelle}</strong>
                          <button
                            type="button"
                            className="secondaire discret"
                            title="Retirer cette machine de l’intervention"
                            aria-label={`Retirer ${m.libelle}`}
                            onClick={() => modifier(retirerMachine(f, m.assetId))}
                          >
                            ✕ Retirer
                          </button>
                        </div>
                        {info === undefined && <p className="aide">Lecture de la gamme…</p>}
                        {info === 'ERREUR' && (
                          <p className="aide">La gamme de cette machine n’a pas pu être lue : l’intervention se crée quand même, sans liste de pièces.</p>
                        )}
                        {info !== undefined && info !== 'ERREUR' && info.paliers.length === 0 && (
                          <p className="aide">
                            {info.gamme
                              ? `Gamme ${info.gamme} : aucune version en vigueur, pas de pièces à préparer.`
                              : 'Aucune gamme rattachée : pas de pièces à préparer.'}
                          </p>
                        )}
                        {info !== undefined && info !== 'ERREUR' && info.paliers.length > 0 && (
                          <div className="champ">
                            <label htmlFor={`palier-${m.assetId}`}>
                              Quel palier ? <span className="aide">({info.gamme})</span>
                            </label>
                            <select
                              id={`palier-${m.assetId}`}
                              value={m.levelKey ?? ''}
                              onChange={(e) => modifier(choisirPalier(f, m.assetId, e.target.value === '' ? null : e.target.value))}
                            >
                              <option value="">Choisir…</option>
                              {info.paliers.map((p) => (
                                <option key={p.levelKey} value={p.levelKey}>
                                  {p.libelle} — {phrasePieces(p.pieces)}
                                </option>
                              ))}
                            </select>
                            {info.proposition && m.levelKey !== info.proposition.levelKey && (
                              <p className="aide">
                                Proposition d’après les heures : {info.proposition.raison}.{' '}
                                <button
                                  type="button"
                                  className="secondaire discret"
                                  onClick={() => info.proposition && modifier(choisirPalier(f, m.assetId, info.proposition.levelKey))}
                                >
                                  Choisir le palier {info.proposition.code}
                                </button>
                              </p>
                            )}
                            <p className="aide">Sans palier choisi, l’intervention n’a pas de liste de pièces à préparer.</p>
                          </div>
                        )}
                        <PiecesDemandeesALaCreation
                          machineId={m.assetId}
                          pieces={enPlus[m.assetId] ?? []}
                          desactive={enCours}
                          onChange={(pieces) => setEnPlus((courant) => ({ ...courant, [m.assetId]: pieces }))}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              {machinesDuSite !== null && machinesDuSite.length > 0 && (
                <details className="visite-machines-site" open={f.machines.length === 0}>
                  <summary>Machines de ce site ({machinesDuSite.length})</summary>
                  <ul className="liste-choix-machine">
                    {machinesDuSite.map((a) => {
                      const cochee = f.machines.some((m) => m.assetId === a.id);
                      return (
                        <li key={a.id}>
                          <label className="visite-machine-case">
                            <input type="checkbox" checked={cochee} onChange={(e) => cocherMachineDuSite(a, e.target.checked)} />
                            <span>
                              <strong>{machineChoisie(a).libelle}</strong>
                              {a.model && a.label ? <span className="aide"> · {a.model}</span> : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </details>
              )}
              <p className="aide">
                Une intervention est un seul déplacement : toutes ses machines sont du même site. Sans machine cochée,
                l’intervention porte sur le site entier.
              </p>
            </div>
          )}

          {refus && <div className="message erreur">{refus}</div>}
          {erreurRecherche && <div className="message erreur">{erreurRecherche}</div>}

          {!aUnLieu && resultats !== null && resultats.machines.length === 0 && resultats.sites.length === 0 && (
            <div className="vide">Rien ne correspond. Essayez un autre mot du nom, du modèle ou de la ville.</div>
          )}
          {!aUnLieu && resultats !== null && resultats.machines.length > 0 && (
            <>
              <p className="aide visite-groupe">Machines</p>
              <ul className="liste-choix-machine">
                {resultats.machines.map((a) => (
                  <li key={a.id}>
                    <button type="button" className="choix-machine" onClick={() => choisirUneMachine(a)}>
                      <strong>{machineChoisie(a).libelle}</strong>
                      <span className="aide">{[a.customerName, a.siteName].filter(Boolean).join(' · ') || 'Sans client ni site'}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {resultats.plus && <p className="aide">D’autres machines existent : précisez la recherche.</p>}
            </>
          )}
          {!aUnLieu && resultats !== null && resultats.sites.length > 0 && (
            <>
              <p className="aide visite-groupe">Sites</p>
              <ul className="liste-choix-machine">
                {resultats.sites.map((s) => (
                  <li key={s.id}>
                    <button type="button" className="choix-machine" onClick={() => choisirUnSite(s)}>
                      <strong>{s.name}</strong>
                      <span className="aide">{[s.customerName, s.city].filter(Boolean).join(' · ') || 'Sans client ni ville'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="champ">
            <label>Quoi ?</label>
            {natures === null ? (
              <p className="aide">Chargement…</p>
            ) : natures.length === 0 ? (
              <p className="aide">Aucune nature de bon : installez les natures de départ depuis « Interventions › Réglages des bons ».</p>
            ) : (
              <div className="chips-secondaires" role="group" aria-label="Nature de l’intervention">
                {natures.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`chip${f.natureId === n.id ? ' actif' : ''}`}
                    aria-pressed={f.natureId === n.id}
                    onClick={() => modifier({ ...f, natureId: n.id })}
                  >
                    {n.label}
                  </button>
                ))}
              </div>
            )}
            {indice && <p className="aide">Prochain entretien de cette machine : {indice}</p>}
          </div>
        </>
      )}

      <div className="champ">
        <label htmlFor="visite-duree">Durée prévue (facultative)</label>
        <input
          id="visite-duree"
          type="text"
          inputMode="decimal"
          value={f.dureeTexte}
          placeholder="8, 7,5, 1h30, 45 min"
          onChange={(e) => modifier({ ...f, dureeTexte: e.target.value })}
        />
        <p className="aide">En heures par défaut : « 8 » = 8 h. Une estimation, jamais un temps réel.</p>
      </div>

      <div className="champ">
        <label>Quand ?</label>
        <div className="chips-secondaires" role="group" aria-label="Quand">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              className={`chip${f.mode === m ? ' actif' : ''}`}
              aria-pressed={f.mode === m}
              onClick={() => modifier(choisirMode(f, m))}
            >
              {LIBELLE_MODE[m]}
            </button>
          ))}
        </div>

        {f.mode === 'FENETRE' ? (
          <div className="ligne-champs">
            <div className="champ">
              <label htmlFor="visite-debut">À faire à partir du</label>
              <input id="visite-debut" type="date" value={f.fenetreDebut} onChange={(e) => modifier({ ...f, fenetreDebut: e.target.value })} />
            </div>
            <div className="champ">
              <label htmlFor="visite-fin">Au plus tard le</label>
              <input id="visite-fin" type="date" value={f.fenetreFin} onChange={(e) => modifier({ ...f, fenetreFin: e.target.value })} />
            </div>
          </div>
        ) : (
          <div className="champ">
            <label htmlFor="visite-date">{f.mode === 'DATE_FIXE' ? 'Date fixée' : 'Date'}</label>
            <input id="visite-date" type="date" value={f.date} onChange={(e) => modifier({ ...f, date: e.target.value })} />
            {f.mode === 'DATE_FIXE' && (
              <p className="aide">L’intervention apparaît sur la ligne « À affecter » à cette date, jusqu’à ce que vous choisissiez le technicien.</p>
            )}
          </div>
        )}
        {f.mode === 'FENETRE' && (
          <p className="aide">Sans date : l’intervention attend dans la liste « À affecter » jusqu’à ce qu’on la place.</p>
        )}
      </div>

      {technicienUtile && (
        <div className="champ">
          <label>{f.mode === 'FENETRE' ? 'Technicien prévu (facultatif)' : 'Qui ?'}</label>
          {techniciens === null ? (
            <p className="aide">Chargement…</p>
          ) : techniciens.length === 0 ? (
            <p className="aide">Aucun technicien : créez-en depuis le bouton « Techniciens » de la page.</p>
          ) : (
            <div className="chips-secondaires" role="group" aria-label="Techniciens">
              {techniciens.map((t) => {
                const actif = f.technicienIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`chip${actif ? ' actif' : ''}`}
                    aria-pressed={actif}
                    onClick={() => modifier(basculerTechnicienFormulaire(f, t.id))}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <label className="case-a-cocher">
        <input type="checkbox" checked={f.tentative} onChange={(e) => modifier({ ...f, tentative: e.target.checked })} /> À confirmer (pré-planifiée : la date n’est pas définitive)
      </label>

      <div className="champ">
        <label htmlFor="visite-commande">N° de commande du client (facultatif)</label>
        <input
          id="visite-commande"
          type="text"
          maxLength={60}
          value={f.commande}
          onChange={(e) => modifier({ ...f, commande: e.target.value })}
        />
      </div>

      <div className="champ">
        <label htmlFor="visite-description">Ce que le client a dit (facultatif)</label>
        <textarea
          id="visite-description"
          rows={3}
          maxLength={1000}
          value={f.description}
          placeholder="Le motif de l’appel, une consigne, ce qu’il faut apporter…"
          onChange={(e) => modifier({ ...f, description: e.target.value })}
        />
      </div>

      {tente && messages.length > 0 && (
        <div className="message erreur">
          <ul className="liste-messages">
            {messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
    </Panneau>
  );
}
